/**
 * StoreOrderInfo Component
 * Displays sales rep contact info, order cutoff countdown, and delivery days
 * Shared between CartOffcanvas and Checkout page
 */

import { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

const StoreOrderInfo = ({ variant = 'default', showTitle = true }) => {
  const { store } = useAuth();
  const [now, setNow] = useState(new Date());

  // Update time every second for live countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  // Format delivery days from route data
  const deliveryDays = useMemo(() => {
    if (!store?.route?.routeDays?.length) return [];

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return store.route.routeDays
      .map(rd => {
        const dayId = rd.optDay?.id || rd.optDayID;
        if (dayId >= 1 && dayId <= 7) {
          return rd.optDay?.name || dayNames[dayId - 1] || dayNames[dayId];
        }
        return rd.optDay?.name;
      })
      .filter(Boolean);
  }, [store?.route?.routeDays]);

  // Calculate dynamic order cutoff countdown based on next delivery date
  // Logic: cutoffHours before delivery day START (midnight)
  // e.g., 24 hours cutoff + Wednesday delivery = must order by Monday 11:59 PM
  const deliveryInfo = useMemo(() => {
    // Helper: Get week of month (1-4) for a given date
    const getWeekOfMonth = (date) => {
      const dayOfMonth = date.getDate();
      if (dayOfMonth <= 7) return 1;
      if (dayOfMonth <= 14) return 2;
      if (dayOfMonth <= 21) return 3;
      return 4;
    };

    // Helper: Parse schedule name to get valid weeks
    const getValidWeeksFromScheduleName = (scheduleName) => {
      if (!scheduleName) return [1, 2, 3, 4]; // All weeks if no schedule

      const name = scheduleName.toLowerCase();

      // Bi-weekly schedules
      if (name.includes('1st') && name.includes('3rd')) return [1, 3];
      if (name.includes('2nd') && name.includes('4th')) return [2, 4];

      // Monthly schedules - single week
      if (name.includes('1st')) return [1];
      if (name.includes('2nd')) return [2];
      if (name.includes('3rd')) return [3];
      if (name.includes('4th')) return [4];

      return [1, 2, 3, 4]; // Default to all weeks
    };

    // Helper: Check if a date matches the route's frequency and schedule
    const isDateValidForSchedule = (date, frequencyId, scheduleName) => {
      // Weekly (frequencyId = 1) or no frequency - all dates valid
      if (!frequencyId || frequencyId === 1) return true;

      const weekOfMonth = getWeekOfMonth(date);
      const validWeeks = getValidWeeksFromScheduleName(scheduleName);

      return validWeeks.includes(weekOfMonth);
    };

    if (!store?.route?.routeDays?.length) return null;

    // Build per-day cutoff lookup from OrderCutoffs
    const orderCutoffs = store.company?.orderCutoffs || [];
    const cutoffByDay = {};
    orderCutoffs.forEach(oc => {
      cutoffByDay[oc.optDayID] = oc.hours;
    });
    const timezone = store.company?.optTimeZone?.timeZone || 'America/Chicago';
    const frequencyId = store.route?.optRouteFrequency?.id;
    const scheduleName = store.route?.optRouteSchedule?.name;

    // Get current time in company's timezone
    let companyNow;
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const parts = formatter.formatToParts(now);
      const dateParts = {};
      parts.forEach(part => {
        dateParts[part.type] = part.value;
      });
      companyNow = new Date(
        `${dateParts.year}-${dateParts.month}-${dateParts.day}T${dateParts.hour}:${dateParts.minute}:${dateParts.second}`
      );
    } catch (e) {
      console.warn('Invalid timezone, falling back to local time:', timezone);
      companyNow = now;
    }

    const routeDayIds = store.route.routeDays.map((rd) => rd.optDayID);

    // Find the next delivery date (check up to 35 days for monthly routes)
    for (let i = 0; i < 35; i++) {
      const checkDate = new Date(companyNow);
      checkDate.setDate(companyNow.getDate() + i);
      checkDate.setHours(0, 0, 0, 0);

      const dayOfWeek = checkDate.getDay() + 1; // 1=Sunday, 2=Monday, etc.

      // Check if this day of week is a route day AND matches the frequency/schedule
      if (routeDayIds.includes(dayOfWeek) && isDateValidForSchedule(checkDate, frequencyId, scheduleName)) {
        // Calculate cutoff deadline using per-day cutoff hours:
        // cutoffHours before delivery day START (midnight)
        // e.g., 24 hours cutoff + Wednesday delivery = Tuesday 12:00 AM midnight
        // Orders must be placed BEFORE this time (i.e., by Monday 11:59:59 PM)
        const cutoffHours = cutoffByDay[dayOfWeek] ?? 0;
        const deliveryDayStart = new Date(checkDate); // midnight of delivery day
        const cutoffDeadline = new Date(deliveryDayStart.getTime() - (cutoffHours * 60 * 60 * 1000));

        if (companyNow < cutoffDeadline) {
          // Calculate time remaining until cutoff deadline
          const msRemaining = cutoffDeadline.getTime() - companyNow.getTime();
          const hoursRemaining = Math.floor(msRemaining / (1000 * 60 * 60));
          const minutesRemaining = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60));
          const secondsRemaining = Math.floor((msRemaining % (1000 * 60)) / 1000);

          // Format delivery date
          const deliveryDateFormatted = checkDate.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'short',
            day: 'numeric'
          });

          return {
            deliveryDate: deliveryDateFormatted,
            hoursRemaining,
            minutesRemaining,
            secondsRemaining,
            isUrgent: hoursRemaining < 4,
            isCritical: hoursRemaining < 1
          };
        }
      }
    }

    return null;
  }, [store?.route?.routeDays, store?.route?.optRouteFrequency, store?.route?.optRouteSchedule, store?.company?.orderCutoffs, store?.company?.optTimeZone, now]);

  const salesRep = store?.salesRep;

  // Don't render if no useful info available
  if (!salesRep && !deliveryInfo && deliveryDays.length === 0) {
    return null;
  }

  // Format countdown display
  const getCountdownDisplay = () => {
    if (!deliveryInfo) return null;

    const { hoursRemaining, minutesRemaining, secondsRemaining } = deliveryInfo;

    // Pad seconds for consistent display
    const paddedSeconds = String(secondsRemaining).padStart(2, '0');

    if (hoursRemaining >= 48) {
      const days = Math.floor(hoursRemaining / 24);
      const remainingHours = hoursRemaining % 24;
      return `${days} days ${remainingHours} hrs`;
    }

    if (hoursRemaining > 0) {
      const paddedMinutes = String(minutesRemaining).padStart(2, '0');
      return `${hoursRemaining} hrs ${paddedMinutes} min`;
    }

    // Under 1 hour — show seconds counting down
    return `${minutesRemaining}:${paddedSeconds}`;
  };

  // Compact variant for tight spaces
  if (variant === 'compact') {
    return (
      <div className="store-order-info store-order-info--compact">
        {salesRep && (
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-person-badge text-primary"></i>
            <small>
              <strong>{salesRep.name}</strong>
              {salesRep.phone && (
                <a href={`tel:${salesRep.phone}`} className="ms-2 text-muted">
                  {salesRep.phone}
                </a>
              )}
            </small>
          </div>
        )}
        {deliveryInfo && (
          <div className={`d-flex align-items-center gap-2 mb-2 ${deliveryInfo.isCritical ? 'text-danger' : ''}`}>
            <i className={`bi bi-clock-history ${deliveryInfo.isCritical ? 'text-danger' : deliveryInfo.isUrgent ? 'text-warning' : 'text-success'}`}></i>
            <small>
              Order in <strong>{getCountdownDisplay()}</strong> for <strong>{deliveryInfo.deliveryDate}</strong> delivery
            </small>
          </div>
        )}
        {deliveryDays.length > 0 && (
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-truck text-success"></i>
            <small>
              Delivery Days: <strong>{deliveryDays.join(', ')}</strong>
              {/* Show frequency and schedule for non-weekly routes */}
              {store?.route?.optRouteFrequency?.id && store.route.optRouteFrequency.id !== 1 && (
                <span className="text-muted ms-1">
                  ({store.route.optRouteFrequency.name}{store.route.optRouteSchedule?.name ? ` - ${store.route.optRouteSchedule.name}` : ''})
                </span>
              )}
            </small>
          </div>
        )}
      </div>
    );
  }

  // Default card variant
  return (
    <div className="store-order-info card border-0 bg-light">
      <div className="card-body py-3">
        {showTitle && (
          <h6 className="card-title mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Order Information
          </h6>
        )}

        {/* Order Countdown - Amazon style */}
        {deliveryInfo && (
          <div className={`mb-3 p-2 rounded ${deliveryInfo.isCritical ? 'bg-danger bg-opacity-10' : deliveryInfo.isUrgent ? 'bg-warning bg-opacity-10' : 'bg-success bg-opacity-10'}`}>
            <div className="d-flex align-items-center gap-2">
              <i className={`bi bi-clock-history fs-5 ${deliveryInfo.isCritical ? 'text-danger' : deliveryInfo.isUrgent ? 'text-warning' : 'text-success'}`}></i>
              <div>
                <span className={deliveryInfo.isCritical ? 'text-danger' : deliveryInfo.isUrgent ? 'text-warning' : ''}>
                  Order in the next <strong>{getCountdownDisplay()}</strong>
                </span>
                <br />
                <span>
                  to receive your order on <strong>{deliveryInfo.deliveryDate}</strong>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Sales Rep */}
        {salesRep && (
          <div className="mb-3">
            <div className="d-flex align-items-start gap-2">
              <i className="bi bi-person-badge text-primary mt-1"></i>
              <div>
                <small className="text-muted d-block">Your Sales Rep</small>
                <strong>{salesRep.name}</strong>
                {salesRep.email && (
                  <div>
                    <a href={`mailto:${salesRep.email}`} className="small text-decoration-none">
                      <i className="bi bi-envelope me-1"></i>
                      {salesRep.email}
                    </a>
                  </div>
                )}
                {salesRep.phone && (
                  <div>
                    <a href={`tel:${salesRep.phone}`} className="small text-decoration-none">
                      <i className="bi bi-telephone me-1"></i>
                      {salesRep.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delivery Days */}
        {deliveryDays.length > 0 && (
          <div>
            <div className="d-flex align-items-start gap-2">
              <i className="bi bi-truck text-success mt-1"></i>
              <div>
                <small className="text-muted d-block">Your Delivery Days</small>
                <strong>{deliveryDays.join(', ')}</strong>
                {/* Show frequency and schedule for non-weekly routes */}
                {store?.route?.optRouteFrequency?.id && store.route.optRouteFrequency.id !== 1 && (
                  <div className="small text-muted mt-1">
                    <i className="bi bi-calendar-week me-1"></i>
                    {store.route.optRouteFrequency.name}
                    {store.route.optRouteSchedule?.name && (
                      <span> - {store.route.optRouteSchedule.name}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreOrderInfo;